import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';

import {
  Brain,
  Calendar,
  CaretDown,
  CircleNotch,
  Code,
  FileArrowUp,
  FileText,
  Flask,
  GearSix,
  Image,
  Info,
  Link,
  Microphone,
  MusicNote,
  PaperPlaneRight,
  Phone,
  Robot,
  Shield,
  Sparkle,
  UploadSimple,
  User,
  Warning,
  WebhooksLogo,
  X,
} from '@phosphor-icons/react';
// ═══════════════════════════════════════════════════════════════════════════════
// CANONICAL INPUT TYPES (V2.1)
// ═══════════════════════════════════════════════════════════════════════════════
const INPUT_TYPE_CONFIG = {
  text: { 
    label: 'Text', 
    icon: FileText, 
    color: 'blue',
    testable: true,
    component: 'textarea'
  },
  file_upload: { 
    label: 'File Upload', 
    icon: FileArrowUp, 
    color: 'purple',
    testable: true,
    component: 'dropzone',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt'
  },
  audio_upload: { 
    label: 'Audio Upload', 
    icon: MusicNote, 
    color: 'pink',
    testable: true,
    component: 'dropzone',
    accept: '.wav,.mp3,.m4a,.ogg,.flac'
  },
  webhook_event: { 
    label: 'Webhook Event', 
    icon: WebhooksLogo, 
    color: 'green',
    testable: true,
    component: 'json_editor'
  },
  api_payload: { 
    label: 'API Payload', 
    icon: Code, 
    color: 'cyan',
    testable: true,
    component: 'json_editor'
  },
  url_fetch: { 
    label: 'URL Fetch', 
    icon: Link, 
    color: 'orange',
    testable: true,
    component: 'url_input'
  },
  // Legacy types mapped to new canonical types
  pdf: { label: 'PDF File', icon: FileText, color: 'red', testable: true, component: 'dropzone', accept: '.pdf' },
  docx: { label: 'Word Doc', icon: FileText, color: 'blue', testable: true, component: 'dropzone', accept: '.doc,.docx' },
  xlsx: { label: 'Excel', icon: FileText, color: 'green', testable: true, component: 'dropzone', accept: '.xls,.xlsx' },
  csv: { label: 'CSV', icon: FileText, color: 'teal', testable: true, component: 'dropzone', accept: '.csv' },
  image: { label: 'Image', icon: Image, color: 'purple', testable: true, component: 'dropzone', accept: '.png,.jpg,.jpeg,.webp,.gif' },
  audio: { label: 'Audio', icon: MusicNote, color: 'pink', testable: true, component: 'dropzone', accept: '.wav,.mp3,.m4a' },
};

// Input types that are NOT testable in console (require special handling)
const NON_TESTABLE_INPUT_TYPES = [
  'voice_realtime', 'phone_call', 'schedule', 'scheduled', 'cron', 'calendar_event'
];

// Legacy mapping for backwards compatibility
const NON_TEXT_INPUT_TYPES = [
  'file_pdf', 'file_docx', 'file_xlsx', 'file_csv', 'file_audio', 'file_video',
  'pdf', 'docx', 'xlsx', 'csv', 'audio', 'video',
  'image', 'image_png', 'image_jpg', 'image_jpeg',
  'webhook', 'webhook_event', 'webhook_payload',
  'schedule', 'scheduled', 'cron'
];

// Map input types to human-readable labels (legacy - kept for backwards compatibility)
const INPUT_TYPE_LABELS = {
  'file_pdf': 'PDF File',
  'file_docx': 'Word Document',
  'file_xlsx': 'Excel Spreadsheet',
  'file_csv': 'CSV File',
  'file_audio': 'Audio File',
  'file_video': 'Video File',
  'pdf': 'PDF File',
  'docx': 'Word Document',
  'xlsx': 'Excel Spreadsheet',
  'csv': 'CSV File',
  'audio': 'Audio File',
  'video': 'Video File',
  'image': 'Image',
  'image_png': 'PNG Image',
  'image_jpg': 'JPG Image',
  'image_jpeg': 'JPEG Image',
  'webhook': 'Webhook',
  'webhook_event': 'Webhook Event',
  'webhook_payload': 'Webhook Payload',
  'schedule': 'Schedule',
  'scheduled': 'Scheduled Trigger',
  'cron': 'Cron Schedule',
  // V2.1 canonical types
  'text': 'Text',
  'file_upload': 'File Upload',
  'audio_upload': 'Audio Upload',
  'api_payload': 'API Payload',
  'url_fetch': 'URL Fetch'
};

// Icons for different input types (legacy - kept for backwards compatibility)
const INPUT_TYPE_ICONS = {
  'file_pdf': FileText,
  'file_docx': FileText,
  'file_xlsx': FileText,
  'file_csv': FileText,
  'file_audio': MusicNote,
  'file_video': FileText,
  'pdf': FileText,
  'docx': FileText,
  'xlsx': FileText,
  'csv': FileText,
  'image': Image,
  'image_png': Image,
  'image_jpg': Image,
  'image_jpeg': Image,
  'webhook': WebhooksLogo,
  'webhook_event': WebhooksLogo,
  'webhook_payload': WebhooksLogo,
  'schedule': Calendar,
  'scheduled': Calendar,
  'cron': Calendar,
  // V2.1 canonical types
  'text': FileText,
  'file_upload': FileArrowUp,
  'audio_upload': MusicNote,
  'api_payload': Code,
  'url_fetch': Link
};

// ═══════════════════════════════════════════════════════════════════════════════
// V2.1 AGENT TESTABILITY HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if agent is a realtime voice agent (S2S)
 */
const isRealtimeVoiceAgent = (agent) => {
  if (!agent) return false;
  return (
    agent.interaction_mode === 'voice_realtime' ||
    agent.voice_config?.enabled ||
    agent.capability_profile === 'live_voice_s2s_agent' ||
    agent.capability_profile === 'voice_output_tts_agent' ||
    agent.capability_profile === 'voice_input_agent'
  );
};

/**
 * Get testable input types for an agent (V2.1)
 * Returns array of input types that can be tested in the console
 */
const getTestableInputTypes = (agent) => {
  if (!agent) return ['text'];
  
  // S2S voice agents cannot be tested here
  if (isRealtimeVoiceAgent(agent)) return [];
  
  const inputTypes = agent.input_types || ['text'];
  
  // Filter to only testable types
  return inputTypes.filter(type => {
    const normalizedType = type.toLowerCase();
    // Exclude non-testable types
    if (NON_TESTABLE_INPUT_TYPES.includes(normalizedType)) return false;
    // Include all other types (we have UI for them now)
    return true;
  });
};

/**
 * Check if agent can be tested in console (V2.1)
 * Now supports file upload, webhook, etc. - only excludes S2S voice
 */
const canTestAgent = (agent) => {
  if (!agent) return false;
  
  // S2S voice agents cannot be tested here
  if (isRealtimeVoiceAgent(agent)) return false;
  
  // Check if agent has at least one testable input type
  const testableTypes = getTestableInputTypes(agent);
  return testableTypes.length > 0;
};

/**
 * Check if an agent supports text-only input (legacy - kept for backwards compatibility)
 */
const isTextOnlyAgent = (agent) => {
  if (!agent) return false;
  
  // Voice agents (by interaction_mode) are not testable here
  if (agent.interaction_mode === 'voice_realtime') return false;
  
  // Legacy voice detection
  if (agent.voice_config?.enabled) return false;
  if (agent.capability_profile === 'live_voice_s2s_agent') return false;
  if (agent.capability_profile === 'voice_output_tts_agent') return false;
  if (agent.capability_profile === 'voice_input_agent') return false;
  
  // Get canonical input_types from API (populated by backend)
  const inputTypes = agent.input_types || ['text'];
  
  // Check if any non-text input types are present
  const hasNonTextInputs = inputTypes.some(type => 
    NON_TEXT_INPUT_TYPES.includes(type.toLowerCase())
  );
  
  return !hasNonTextInputs;
};

/**
 * Get the non-text input types for an agent
 * Uses canonical input_types from API
 */
const getNonTextInputTypes = (agent) => {
  if (!agent) return [];
  
  // Get canonical input_types from API
  const inputTypes = agent.input_types || [];
  
  // Add voice indicator if voice agent
  const types = [...inputTypes];
  if (agent.interaction_mode === 'voice_realtime' ||
      agent.voice_config?.enabled || 
      agent.capability_profile === 'live_voice_s2s_agent' ||
      agent.capability_profile === 'voice_output_tts_agent' ||
      agent.capability_profile === 'voice_input_agent') {
    if (!types.includes('audio') && !types.includes('voice')) {
      types.push('voice');
    }
  }
  
  return types.filter(type => 
    NON_TEXT_INPUT_TYPES.includes(type.toLowerCase()) || type === 'voice'
  );
};

const AgentTestConsole = () => {
  const navigate = useNavigate();
  const { userPlan } = usePlan();
  const { activeOrganization } = useOrganization();
  const [searchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [conversationHistories, setConversationHistories] = useState({});
  const [userPrompt, setUserPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // V2.2: Dynamic input states
  const [selectedInputType, setSelectedInputType] = useState('text');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [webhookJson, setWebhookJson] = useState('{\n  "event": "example",\n  "data": {}\n}');
  const [jsonError, setJsonError] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const hasAccess = userPlan === 'pro' || userPlan === 'partner-pro' || userPlan === 'partner_pro';
    if (hasAccess && activeOrganization) {
      fetchAgents();
    }
  }, [userPlan, activeOrganization]);

  useEffect(() => {
    const agentIdFromUrl = searchParams.get('agentId');
    if (agentIdFromUrl && agents.length > 0) {
      const agent = agents.find(a => a.id === agentIdFromUrl);
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [searchParams, agents]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedAgent, conversationHistories]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const fetchAgents = async () => {
    if (!activeOrganization) return;
    
    try {
      const url = `${BACKEND_URL}/api/agents?status=active`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'X-Organization-ID': activeOrganization.organization_id
        }
      });
      if (response.ok) {
        const data = await response.json();
        const activeAgents = data.agents || [];
        setAgents(activeAgents);
        if (activeAgents.length > 0 && !selectedAgent) {
          setSelectedAgent(activeAgents[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const getCurrentMessages = () => {
    if (!selectedAgent) return [];
    return conversationHistories[selectedAgent.id] || [];
  };

  const sendMessage = async () => {
    if (!userPrompt.trim() || !selectedAgent || !activeOrganization || loading) return;
    if (!isTextOnlyAgent(selectedAgent)) return;

    const messageText = userPrompt.trim();
    setUserPrompt('');
    setLoading(true);

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setConversationHistories(prev => ({
      ...prev,
      [selectedAgent.id]: [...(prev[selectedAgent.id] || []), userMessage]
    }));

    try {
      const url = `${BACKEND_URL}/api/agents/${selectedAgent.id}/chat`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrganization.organization_id
        },
        credentials: 'include',
        body: JSON.stringify({
          message: messageText,
          context: {},
          // Mark as test mode execution
          executionMode: 'test'
        })
      });

      if (!response.ok) {
        throw new Error(`Chat failed: ${response.status}`);
      }

      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        timestamp: data.timestamp,
        metadata: data.metadata,
        isTestRun: true
      };

      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), assistantMessage]
      }));

    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };

      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), errorMessage]
      }));
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // V2.2: DYNAMIC INPUT HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);
  
  const handleFileDrop = useCallback((event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);
  
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);
  
  const clearUploadedFile = useCallback(() => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);
  
  const handleJsonChange = useCallback((value) => {
    setWebhookJson(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError(e.message);
    }
  }, []);
  
  const sendFileMessage = async () => {
    if (!uploadedFile || !selectedAgent || !activeOrganization || loading) return;
    
    setLoading(true);
    
    const userMessage = {
      role: 'user',
      content: `Uploaded file: ${uploadedFile.name}`,
      timestamp: new Date().toISOString(),
      fileInfo: { name: uploadedFile.name, size: uploadedFile.size, type: uploadedFile.type }
    };
    
    setConversationHistories(prev => ({
      ...prev,
      [selectedAgent.id]: [...(prev[selectedAgent.id] || []), userMessage]
    }));
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      // V2.2: Privacy defaults - ephemeral by default
      formData.append('retention_keep_file', 'false');
      formData.append('retention_keep_transcript', 'false');
      formData.append('enable_side_effects', 'false');
      
      // V2.2: Use new test endpoint
      const url = `${BACKEND_URL}/api/agents/${selectedAgent.id}/test/process-file`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Organization-ID': activeOrganization.organization_id
        },
        body: formData
      });
      
      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response || data.error || 'File processed successfully.',
        timestamp: new Date().toISOString(),
        isTestMode: true
      };
      
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), assistantMessage]
      }));
      
      clearUploadedFile();
    } catch (error) {
      console.error('Error processing file:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Error processing file. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), errorMessage]
      }));
    } finally {
      setLoading(false);
    }
  };
  
  const sendWebhookTest = async () => {
    if (!webhookJson || jsonError || !selectedAgent || !activeOrganization || loading) return;
    
    setLoading(true);
    
    let payload;
    try {
      payload = JSON.parse(webhookJson);
    } catch (e) {
      setJsonError('Invalid JSON');
      setLoading(false);
      return;
    }
    
    const userMessage = {
      role: 'user',
      content: `Webhook Event:\n\`\`\`json\n${JSON.stringify(payload, null, 2)}\n\`\`\``,
      timestamp: new Date().toISOString()
    };
    
    setConversationHistories(prev => ({
      ...prev,
      [selectedAgent.id]: [...(prev[selectedAgent.id] || []), userMessage]
    }));
    
    try {
      // V2.2: Use new test endpoint
      const url = `${BACKEND_URL}/api/agents/${selectedAgent.id}/test/webhook`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrganization.organization_id
        },
        body: JSON.stringify({
          payload,
          execution_mode: 'test',
          enable_side_effects: false  // V2.2: Side effects OFF by default
        })
      });
      
      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response || JSON.stringify(data, null, 2),
        timestamp: new Date().toISOString(),
        isTestMode: true
      };
      
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), assistantMessage]
      }));
    } catch (error) {
      console.error('Error testing webhook:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Error testing webhook. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), errorMessage]
      }));
    } finally {
      setLoading(false);
    }
  };
  
  const sendUrlFetch = async () => {
    if (!urlInput.trim() || !selectedAgent || !activeOrganization || loading) return;
    
    setLoading(true);
    
    const userMessage = {
      role: 'user',
      content: `Fetch URL: ${urlInput}`,
      timestamp: new Date().toISOString()
    };
    
    setConversationHistories(prev => ({
      ...prev,
      [selectedAgent.id]: [...(prev[selectedAgent.id] || []), userMessage]
    }));
    
    try {
      // V2.2: Use new test endpoint
      const url = `${BACKEND_URL}/api/agents/${selectedAgent.id}/test/fetch-url`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrganization.organization_id
        },
        body: JSON.stringify({
          url: urlInput,
          execution_mode: 'test'
        })
      });
      
      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.response || data.error || 'URL processed.',
        timestamp: new Date().toISOString(),
        isTestMode: true
      };
      
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), assistantMessage]
      }));
      
      setUrlInput('');
    } catch (error) {
      console.error('Error fetching URL:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Error fetching URL. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setConversationHistories(prev => ({
        ...prev,
        [selectedAgent.id]: [...(prev[selectedAgent.id] || []), errorMessage]
      }));
    } finally {
      setLoading(false);
    }
  };
  
  // Get available input types for current agent
  const getAvailableInputTypes = () => {
    if (!selectedAgent) return ['text'];
    const testable = getTestableInputTypes(selectedAgent);
    return testable.length > 0 ? testable : ['text'];
  };

  const handleAgentChange = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      setSelectedAgent(agent);
      // Reset input type to first available for new agent
      const available = getTestableInputTypes(agent);
      if (available.length > 0 && !available.includes(selectedInputType)) {
        setSelectedInputType(available[0]);
      }
    }
  };

  const hasAccess = userPlan === 'pro' || userPlan === 'partner-pro' || userPlan === 'partner_pro';
  
  if (!hasAccess) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="glass rounded-3xl p-12">
            <Flask size={64} className="mx-auto mb-6 text-[#6366F1]" />
            <h1 className="text-4xl font-bold text-white mb-4">
              Agent Test Console
            </h1>
            <p className="text-[#A0AEC0] text-lg mb-8">
              Safely test text-based AI agents before deploying them to your workspace. This feature is available to Pro and Partner Pro members.
            </p>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="px-8 py-4 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-bold hover:opacity-90 transition-opacity"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentMessages = getCurrentMessages();
  const agentIsTextOnly = selectedAgent ? isTextOnlyAgent(selectedAgent) : true;
  const nonTextInputTypes = selectedAgent ? getNonTextInputTypes(selectedAgent) : [];
  const isS2SVoiceAgent = selectedAgent ? isRealtimeVoiceAgent(selectedAgent) : false;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header Bar */}
      <div className="border-b border-[rgba(255,255,255,0.05)] bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
                <Flask size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Agent Test Console</h1>
                <p className="text-xs text-gray-500">Safely test text-based AI agents before deploying them to your workspace</p>
              </div>
            </div>
            
            {/* Agent Selector */}
            {agents.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-gray-400">Select an agent to test:</span>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedAgent?.id || ''}
                    onChange={(e) => handleAgentChange(e.target.value)}
                    className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] text-white text-sm focus:outline-none focus:border-[#6366F1] cursor-pointer min-w-[280px]"
                    data-testid="agent-selector"
                  >
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name} {!isTextOnlyAgent(agent) ? '⚠️' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedAgent && agentIsTextOnly && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.05)]">
                        {selectedAgent.llm_provider}
                      </span>
                      <span className={`px-2 py-0.5 rounded ${
                        selectedAgent.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-400'
                      }`}>
                        {selectedAgent.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Test Mode Info Banner */}
      <div className="max-w-4xl mx-auto px-6 mt-4">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Shield size={16} className="text-amber-400" />
          </div>
          <div>
            <span className="text-amber-400 font-semibold text-sm">Test Mode</span>
            <p className="text-amber-300/80 text-xs">
              Runs executed here are sandboxed and do not affect dashboards, automations, ROI metrics, or production data.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6">
        {/* Chat Area */}
        <div className="flex flex-col" style={{ height: 'calc(100vh - 260px)' }}>
          {agents.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Brain size={48} className="mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-semibold text-white mb-2">No Active Agents</h3>
                <p className="text-sm text-gray-400">Create and activate an agent to get started</p>
              </div>
            </div>
          ) : !canTestAgent(selectedAgent) && selectedAgent ? (
            /* V2.2: Truly Unsupported Agent State (S2S voice only) */
            <div className="flex-1 flex items-center justify-center">
              <div className="max-w-md p-8 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] rounded-2xl text-center">
                {isS2SVoiceAgent ? (
                  /* Real-time Voice (S2S) Agent */
                  <>
                    <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                      <Phone size={32} className="text-purple-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      Real-Time Voice Agent
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      This is a live voice agent that handles real-time phone conversations.
                    </p>
                    
                    {/* Voice Agent Badge */}
                    <div className="flex flex-wrap gap-2 justify-center mb-6">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm">
                        <Microphone size={14} />
                        <span>Live Voice (S2S)</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-500 text-xs mb-6">
                      Voice agents cannot be tested via text input. To test this agent, use the Voice Test Mode on the agent configuration page, or make a test call to the assigned phone number.
                    </p>
                  </>
                ) : (
                  /* Scheduled/Cron Agents */
                  <>
                    <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                      <Calendar size={32} className="text-orange-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      Scheduled Agent
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      This agent runs on a schedule and cannot be manually triggered here.
                    </p>
                    <p className="text-gray-500 text-xs mb-6">
                      To test this agent, modify its schedule temporarily or wait for its next scheduled run.
                    </p>
                  </>
                )}
                
                <button
                  onClick={() => navigate(`/partner-pro/ai-workforce/agents/${selectedAgent.id}`)}
                  className="px-6 py-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-sm font-medium hover:bg-[rgba(255,255,255,0.1)] transition-colors flex items-center gap-2 mx-auto"
                  data-testid="view-agent-config-btn"
                >
                  <GearSix size={16} />
                  View Agent Configuration
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Messages Area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto py-6 space-y-3">
                {!selectedAgent ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Sparkle size={40} className="mx-auto mb-3 text-gray-600" />
                      <p className="text-sm text-gray-400">Select an agent to start testing</p>
                    </div>
                  </div>
                ) : currentMessages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Flask size={40} className="mx-auto mb-3 text-[#6366F1]" />
                      <p className="text-sm text-gray-400">Enter a test prompt for {selectedAgent.name}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {currentMessages.map((msg, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Robot size={14} className="text-white" />
                          </div>
                        )}
                        
                        <div className={`max-w-[75%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                          {/* Test Run Label for assistant messages */}
                          {msg.role === 'assistant' && !msg.isError && (
                            <div className="flex items-center gap-1 mb-1 ml-1">
                              <Flask size={10} className="text-amber-400" />
                              <span className="text-[10px] text-amber-400 font-medium">Test Run — Not a Production Execution</span>
                            </div>
                          )}
                          <div
                            className={`px-4 py-2.5 rounded-xl text-sm ${
                              msg.role === 'user'
                                ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
                                : msg.isError
                                ? 'bg-red-500/20 border border-red-500/30 text-red-300'
                                : 'glass text-white border border-[rgba(255,255,255,0.1)]'
                            }`}
                          >
                            <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                          </div>
                        </div>

                        {msg.role === 'user' && (
                          <div className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,0.05)] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <User size={14} className="text-white" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Loading indicator */}
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3 justify-start"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                      <Robot size={16} className="text-white" />
                    </div>
                    <div className="glass p-4 rounded-2xl border border-[rgba(255,255,255,0.1)]">
                      <div className="flex gap-2">
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                          className="w-2 h-2 rounded-full bg-[#6366F1]"
                        />
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                          className="w-2 h-2 rounded-full bg-[#6366F1]"
                        />
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                          className="w-2 h-2 rounded-full bg-[#6366F1]"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* V2.2: Dynamic Input Area */}
              <div className="p-4 bg-[#0A0A0A] border-t border-[rgba(255,255,255,0.05)]">
                {/* Input Type Selector (V2.2) - Only show if agent has multiple input types */}
                {selectedAgent && getAvailableInputTypes().length > 1 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {getAvailableInputTypes().map(type => {
                      const config = INPUT_TYPE_CONFIG[type] || INPUT_TYPE_CONFIG.text;
                      const Icon = config.icon || FileText;
                      const isActive = selectedInputType === type;
                      return (
                        <button
                          key={type}
                          onClick={() => setSelectedInputType(type)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                            isActive
                              ? 'bg-[#6366F1]/20 text-[#6366F1] border border-[#6366F1]/30'
                              : 'bg-[rgba(255,255,255,0.03)] text-gray-400 border border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.05)]'
                          }`}
                        >
                          <Icon size={12} />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Text Input (default) */}
                {selectedInputType === 'text' && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
                      placeholder={selectedAgent ? `Enter a test prompt for this agent…` : "Select an agent first"}
                      disabled={!selectedAgent || loading}
                      className="flex-1 px-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.03)] text-white text-sm border border-[rgba(255,255,255,0.05)] placeholder-gray-600 focus:outline-none focus:border-[#6366F1] focus:bg-[rgba(255,255,255,0.05)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="test-prompt-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!selectedAgent || !userPrompt.trim() || loading}
                      className="px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#5558E3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="send-test-btn"
                    >
                      {loading ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} />}
                    </button>
                  </div>
                )}
                
                {/* File UploadSimple Dropzone (V2.2) */}
                {(selectedInputType === 'file_upload' || selectedInputType === 'audio_upload') && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept={INPUT_TYPE_CONFIG[selectedInputType]?.accept || '*'}
                      className="hidden"
                    />
                    
                    {!uploadedFile ? (
                      <div
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-[rgba(255,255,255,0.1)] rounded-xl p-8 text-center cursor-pointer hover:border-[#6366F1]/50 hover:bg-[rgba(99,102,241,0.05)] transition-all"
                      >
                        <UploadSimple size={32} className="mx-auto mb-3 text-gray-500" />
                        <p className="text-sm text-gray-400 mb-1">
                          Drop a file here or click to select
                        </p>
                        <p className="text-xs text-gray-600">
                          {selectedInputType === 'audio_upload' 
                            ? 'Supported: WAV, MP3, M4A, OGG, FLAC'
                            : 'Supported: PDF, DOCX, XLSX, CSV, TXT'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-[rgba(255,255,255,0.03)] rounded-xl border border-[rgba(255,255,255,0.05)]">
                        <div className="w-10 h-10 rounded-lg bg-[#6366F1]/20 flex items-center justify-center">
                          {selectedInputType === 'audio_upload' ? <MusicNote size={20} className="text-[#6366F1]" /> : <FileArrowUp size={20} className="text-[#6366F1]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{uploadedFile.name}</p>
                          <p className="text-xs text-gray-500">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button
                          onClick={clearUploadedFile}
                          className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] text-gray-400 hover:text-white transition-colors"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={sendFileMessage}
                          disabled={loading}
                          className="px-4 py-2 rounded-lg bg-[#6366F1] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#5558E3] transition-colors disabled:opacity-50"
                        >
                          {loading ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} />}
                          Process
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* WebhooksLogo/API JSON Editor (V2.2) */}
                {(selectedInputType === 'webhook_event' || selectedInputType === 'api_payload') && (
                  <div>
                    <div className="relative">
                      <textarea
                        value={webhookJson}
                        onChange={(e) => handleJsonChange(e.target.value)}
                        placeholder='{"event": "example", "data": {}}'
                        rows={6}
                        className={`w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.03)] text-white text-sm font-mono border ${
                          jsonError ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.05)]'
                        } placeholder-gray-600 focus:outline-none focus:border-[#6366F1] resize-none`}
                        data-testid="webhook-json-input"
                      />
                      {jsonError && (
                        <p className="absolute bottom-2 left-4 text-xs text-red-400">
                          Invalid JSON: {jsonError}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-gray-500">
                        {selectedInputType === 'webhook_event' ? 'Simulate a webhook event payload' : 'Send an API payload'}
                      </p>
                      <button
                        onClick={sendWebhookTest}
                        disabled={loading || jsonError}
                        className="px-4 py-2 rounded-lg bg-[#6366F1] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#5558E3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? <CircleNotch size={16} className="animate-spin" /> : <WebhooksLogo size={16} />}
                        Test WebhooksLogo
                      </button>
                    </div>
                  </div>
                )}
                
                {/* URL Input (V2.2) */}
                {selectedInputType === 'url_fetch' && (
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Link size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="url"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !loading && sendUrlFetch()}
                        placeholder="https://example.com/content"
                        disabled={!selectedAgent || loading}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.03)] text-white text-sm border border-[rgba(255,255,255,0.05)] placeholder-gray-600 focus:outline-none focus:border-[#6366F1] focus:bg-[rgba(255,255,255,0.05)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="url-fetch-input"
                      />
                    </div>
                    <button
                      onClick={sendUrlFetch}
                      disabled={!selectedAgent || !urlInput.trim() || loading}
                      className="px-4 py-2.5 rounded-lg bg-[#6366F1] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#5558E3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? <CircleNotch size={16} className="animate-spin" /> : <Link size={16} />}
                      Fetch
                    </button>
                  </div>
                )}
                
                {/* Output Disclaimer */}
                <p className="text-[10px] text-gray-600 mt-2 text-center">
                  Outputs generated here are for validation only and are not persisted to dashboards or workflows.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentTestConsole;
